import { createChecklist, type ChecklistConfig } from '@tour-kit/checklists';

import {
  getStartedTaskOrder,
  type WorkspaceIntent,
} from '@/core/components/onboarding/onboarding-intent';
import {
  GET_STARTED_CHECKLIST_ID,
  GET_STARTED_TASK,
  type GetStartedTaskId,
} from '@/core/guidance/constants';
import type { GetStartedDomainTruth } from '@/core/guidance/use-get-started-domain';
import { shellPaths } from '@/core/router/app-paths';

export type GetStartedLabels = {
  title: string;
  description: string;
  tasks: Record<
    GetStartedTaskId,
    {
      title: string;
      description: string;
    }
  >;
};

export type GetStartedActions = {
  navigate: (path: string) => void;
  startOrientationTour: () => void;
};

function isSkipped(
  taskId: GetStartedTaskId,
  skipped: ReadonlySet<GetStartedTaskId>,
): boolean {
  return skipped.has(taskId);
}

/**
 * Build the get-started checklist. Visibility is baked into the task list
 * (intent + plan entitlements + permissions + skips) so progress never
 * counts hidden tasks.
 */
export function buildGetStartedChecklist(options: {
  labels: GetStartedLabels;
  domain: GetStartedDomainTruth;
  skipped: ReadonlySet<GetStartedTaskId>;
  actions: GetStartedActions;
  intent?: WorkspaceIntent | null;
}): ChecklistConfig {
  const { labels, domain, skipped, actions, intent } = options;
  const tasks: ChecklistConfig['tasks'] = [];

  const pathOrActionFor = (
    id: GetStartedTaskId,
  ): { visible: boolean; pathOrAction: string | (() => void) } => {
    switch (id) {
      case GET_STARTED_TASK.meProfile:
        return { visible: true, pathOrAction: shellPaths.profiles };
      case GET_STARTED_TASK.meCard:
        return { visible: true, pathOrAction: shellPaths.profilesLinkCard };
      case GET_STARTED_TASK.meTour:
        return {
          visible: true,
          pathOrAction: () => {
            actions.startOrientationTour();
          },
        };
      case GET_STARTED_TASK.meShare:
        return { visible: true, pathOrAction: shellPaths.profiles };
      case GET_STARTED_TASK.wsLocation:
        return {
          visible: domain.canManageLocations,
          pathOrAction: shellPaths.moreWorkspaceLocationCreate,
        };
      case GET_STARTED_TASK.wsProduct:
        return {
          visible: domain.canManageProducts,
          pathOrAction: shellPaths.moreProductsSetup,
        };
      case GET_STARTED_TASK.wsGoogle:
        return {
          visible: domain.canSetupReputation,
          pathOrAction: shellPaths.reputationSetup,
        };
      case GET_STARTED_TASK.wsInvite:
        return {
          visible: domain.canInviteMembers,
          pathOrAction: shellPaths.morePeople,
        };
      default:
        return { visible: false, pathOrAction: shellPaths.home };
    }
  };

  for (const id of getStartedTaskOrder(intent)) {
    if (isSkipped(id, skipped)) continue;
    const { visible, pathOrAction } = pathOrActionFor(id);
    if (!visible) continue;
    const copy = labels.tasks[id];
    tasks.push({
      id,
      title: copy.title,
      description: copy.description,
      manualComplete: false,
      action:
        typeof pathOrAction === 'string'
          ? {
              type: 'callback',
              handler: () => {
                actions.navigate(pathOrAction);
              },
            }
          : {
              type: 'callback',
              handler: pathOrAction,
            },
    });
  }

  return createChecklist({
    id: GET_STARTED_CHECKLIST_ID,
    title: labels.title,
    description: labels.description,
    dismissible: true,
    hideOnComplete: true,
    tasks,
  });
}

export function domainCompletedTaskIds(
  domain: GetStartedDomainTruth,
): GetStartedTaskId[] {
  const done: GetStartedTaskId[] = [];
  if (domain.hasLocation) done.push(GET_STARTED_TASK.wsLocation);
  if (domain.hasProduct) done.push(GET_STARTED_TASK.wsProduct);
  if (domain.hasGoogleLinked) done.push(GET_STARTED_TASK.wsGoogle);
  if (domain.hasTeam) done.push(GET_STARTED_TASK.wsInvite);
  if (domain.hasProfile) done.push(GET_STARTED_TASK.meProfile);
  if (domain.hasCard) done.push(GET_STARTED_TASK.meCard);
  return done;
}
